<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue"

interface Tab {
  label: string
}

interface Props {
  tabs: Tab[]
}

const props = defineProps<Props>()

const emit = defineEmits<Emits>()
interface Emits {
  (event: "change", index: number): void
}

const activeTab = ref(0)
const tabIds = computed(() => props.tabs.map((_, index) => `tab-${index}`))
const panelIds = computed(() => props.tabs.map((_, index) => `panel-${index}`))

function selectTab(index: number) {
  if (index >= 0 && index < props.tabs.length) {
    activeTab.value = index
  }
}

async function handleKeydown(event: KeyboardEvent, index: number) {
  let newIndex = index

  switch (event.key) {
    case "ArrowDown":
    case "ArrowRight":
      event.preventDefault()
      newIndex = (index + 1) % props.tabs.length
      break
    case "ArrowUp":
    case "ArrowLeft":
      event.preventDefault()
      newIndex = index === 0 ? props.tabs.length - 1 : index - 1
      break
    case "Home":
      event.preventDefault()
      newIndex = 0
      break
    case "End":
      event.preventDefault()
      newIndex = props.tabs.length - 1
      break
    case "Enter":
    case " ":
      event.preventDefault()
      selectTab(index)
      return
    default:
      return
  }

  selectTab(newIndex)

  // Focus the newly selected tab
  await nextTick()
  const newTab = document.getElementById(tabIds.value[newIndex])
  newTab?.focus()
}

watch(activeTab, (val) => {
  emit("change", val)
},
{ immediate: true }
)
</script>

<template>
  <div class="flex h-full">
    <div
      class="max-w-42 space-y-2"
      role="tablist"
      aria-orientation="vertical"
      aria-label="Content tabs"
    >
      <button
        v-for="(tab, index) in tabs"
        :id="tabIds[index]"
        :key="index"
        :aria-selected="activeTab === index"
        :aria-controls="panelIds[index]"
        :tabindex="activeTab === index ? 0 : -1"
        role="tab"
        class="w-full truncate rounded-lg px-4 py-2 text-left transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:outline-none"
        :class="[
          activeTab === index
            ? 'bg-violet-600 text-white'
            : 'bg-gray-100 text-black hover:bg-gray-300',
        ]"
        @click="selectTab(index)"
        @keydown="handleKeydown($event, index)"
      >
        {{ tab.label }}
      </button>
    </div>

    <div
      :id="panelIds[activeTab]"
      :aria-labelledby="tabIds[activeTab]"
      class="size-full overflow-auto pl-4"
      role="tabpanel"
      tabindex="0"
    >
      <slot :name="`tab-${activeTab}`" />
    </div>
  </div>
</template>
